import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useBrainData } from '../hooks/useBrainData';
import { useBrainPulse } from '../hooks/useBrainPulse';
import { BrainCanvas } from '../components/brain/BrainCanvas';
import { HeartbeatECG } from '../components/brain/HeartbeatECG';
import { CloneAvatarChat } from '../components/brain/CloneAvatarChat';
import { Brain, Zap, Database, Activity, Shield, Users, Play, Square, Send, MessageSquare, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function InfoBadge({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-black/40 backdrop-blur-sm" data-testid={testId}>
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-[11px] text-white/60">{label}</span>
      <span className="text-[11px] font-medium text-white/90">{value}</span>
    </div>
  );
}

function SimulateButton({ token }) {
  const [active, setActive] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);
  const countRef = useRef(0);

  const start = useCallback(() => {
    const TOTAL = 20;
    const INTERVAL_MS = 1500;
    countRef.current = 0;
    setRemaining(TOTAL);
    setActive(true);

    intervalRef.current = setInterval(async () => {
      countRef.current++;
      setRemaining(TOTAL - countRef.current);
      try {
        await fetch(`${API}/api/dashboard/brain-pulse/test`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
      if (countRef.current >= TOTAL) stop();
    }, INTERVAL_MS);
  }, [token]);

  const stop = useCallback(() => {
    clearInterval(intervalRef.current);
    setActive(false);
    setRemaining(0);
  }, []);

  return (
    <button
      data-testid="brain-simulate-btn"
      onClick={active ? stop : start}
      className="flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300"
      style={{
        background: active ? 'rgba(59,130,246,0.2)' : 'rgba(0,0,0,0.5)',
        borderColor: active ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(12px)',
        cursor: 'pointer',
      }}
    >
      {active ? (
        <>
          <Square className="w-3.5 h-3.5 text-blue-400 fill-blue-400" />
          <span className="text-[12px] text-blue-300 font-medium">
            Simulation... {remaining}
          </span>
          {/* Progress ring */}
          <svg width="18" height="18" viewBox="0 0 18 18" className="animate-spin" style={{ animationDuration: '1.5s' }}>
            <circle cx="9" cy="9" r="7" fill="none" stroke="rgba(59,130,246,0.2)" strokeWidth="2" />
            <circle cx="9" cy="9" r="7" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="44" strokeDashoffset={44 - (44 * (20 - remaining) / 20)} strokeLinecap="round" />
          </svg>
        </>
      ) : (
        <>
          <Play className="w-3.5 h-3.5 text-white/70 fill-white/70" />
          <span className="text-[12px] text-white/70 font-medium">Simuler l'activite</span>
        </>
      )}
    </button>
  );
}

export default function CloneBrainPage() {
  const { token, user } = useAuth();
  const { metrics, loading } = useBrainData(token);
  const { pulses, thoughts } = useBrainPulse(token);
  const [cinematicTrigger, setCinematicTrigger] = useState(0);
  const [cinematicActive, setCinematicActive] = useState(false);
  const [cinematicDone, setCinematicDone] = useState(false);

  // Avatar chat overlay — shown by default on page load
  const [showAvatarChat, setShowAvatarChat] = useState(true);

  const handleGoToBrain = useCallback(() => {
    setShowAvatarChat(false);
  }, []);

  // Mini-chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatQuery, setChatQuery] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [thoughtEvent, setThoughtEvent] = useState(null);
  const [clones, setClones] = useState([]);
  const [selectedClone, setSelectedClone] = useState('');
  const [isGalaxyMode, setIsGalaxyMode] = useState(false);
  const chatEndRef = useRef(null);

  // Load clones for the chat
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/persons`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = res.data || [];
        setClones(list);
        if (list.length > 0 && !selectedClone) setSelectedClone(list[0].id);
      })
      .catch(() => {});
  }, [token, selectedClone]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendBrainChat = useCallback(async () => {
    if (!chatQuery.trim() || !selectedClone || chatLoading) return;
    const query = chatQuery.trim();
    setChatQuery('');
    setChatMessages(prev => [...prev, { role: 'user', content: query }]);
    setChatLoading(true);

    // Trigger thought visualization IMMEDIATELY (before response)
    setThoughtEvent({
      id: Date.now(),
      query,
      phase: 'thinking',
      filters: {},
      memoriesUsed: 0,
      responseTimeMs: 0,
    });

    try {
      const res = await axios.post(
        `${API}/api/persons/${selectedClone}/chat`,
        { message: query },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;

      // Update thought with real data
      setThoughtEvent({
        id: Date.now(),
        query,
        phase: 'complete',
        filters: data.retrieval?.resolved_filters || {},
        memoriesUsed: data.memories_used || 0,
        responseTimeMs: data.response_time_ms || 0,
      });

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        memoriesUsed: data.memories_used,
        responseTimeMs: data.response_time_ms,
      }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Erreur de communication avec le clone.' }]);
      setThoughtEvent(null);
    } finally {
      setChatLoading(false);
    }
  }, [chatQuery, selectedClone, chatLoading, token]);

  const dominantColor = useMemo(() => {
    if (!metrics?.sourceList?.length) return '#3b82f6';
    return metrics.sourceList[0]?.color || '#3b82f6';
  }, [metrics]);

  if (loading && !showAvatarChat) {
    return (
      <DashboardLayout title="Cerveau du Clone">
        <div className="flex items-center justify-center h-[70vh]">
          <div className="flex flex-col items-center gap-4">
            <Brain className="w-12 h-12 text-primary animate-pulse" />
            <p className="text-sm text-muted-foreground">Connexion neurale en cours...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Cerveau du Clone">
      <div className="relative w-full" style={{ height: 'calc(100vh - 80px)' }} data-testid="clone-brain-page">
        {/* 3D Canvas + Live Pulse — only mount when avatar chat is dismissed */}
        <div className="absolute inset-0 rounded-xl overflow-hidden bg-[#020810]">
          {!showAvatarChat && (
            <BrainCanvas
              metrics={metrics}
              pulses={pulses}
              liveThoughts={thoughts}
              cinematicTrigger={cinematicTrigger}
              onCinematicEnd={() => { setCinematicActive(false); setCinematicDone(true); }}
              thoughtEvent={thoughtEvent}
              token={token}
              onGalaxyChange={setIsGalaxyMode}
            />
          )}
        </div>

        {/* Avatar Chat Overlay */}
        {showAvatarChat && (
          <CloneAvatarChat
            personId={selectedClone}
            cloneName="Justine BLARY"
            onGoToBrain={handleGoToBrain}
          />
        )}

        {/* Top-left: Title */}
        {!isGalaxyMode && !showAvatarChat && (
        <div className="absolute top-4 left-4 z-10">
          <div className="flex items-center gap-2 mb-1">
            <Brain className="w-5 h-5" style={{ color: dominantColor }} />
            <h2 className="text-lg font-heading font-bold text-white/90" data-testid="brain-title">Cerveau du Clone</h2>
          </div>
          <p className="text-[11px] text-white/40">Visualisation neurale en temps reel</p>
        </div>
        )}

        {/* Top-right: Metric badges */}
        {!isGalaxyMode && !showAvatarChat && (
        <div className="absolute top-4 right-4 z-10 flex flex-wrap gap-2 justify-end max-w-md">
          <InfoBadge icon={Database} label="Memoires" value={(metrics?.totalMemories||0).toLocaleString()} color="#60a5fa" testId="brain-memories" />
          <InfoBadge icon={Users} label="Contacts" value={(metrics?.totalContacts||0).toLocaleString()} color="#a855f7" testId="brain-contacts" />
          <InfoBadge icon={Zap} label="Aujourd'hui" value={metrics?.todayTotal||0} color="#fbbf24" testId="brain-today" />
          <InfoBadge icon={Activity} label="Tasks/24h" value={(metrics?.completedTasks||0).toLocaleString()} color="#22c55e" testId="brain-tasks" />
          <InfoBadge icon={Shield} label="Regles" value="8 + 3" color="#06b6d4" testId="brain-rules" />
        </div>
        )}

        {/* Bottom-right: Simulate button (above ECG) */}
        {!isGalaxyMode && !showAvatarChat && (
        <div className="absolute bottom-16 right-4 z-10 flex flex-col items-end gap-2">
          {!cinematicActive && (
            <button
              data-testid="brain-discover-btn"
              onClick={() => { setCinematicTrigger(t => t + 1); setCinematicActive(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border transition-all duration-300 group"
              style={{
                background: cinematicDone ? 'rgba(0,0,0,0.5)' : 'rgba(96,165,250,0.15)',
                borderColor: cinematicDone ? 'rgba(255,255,255,0.12)' : 'rgba(96,165,250,0.4)',
                backdropFilter: 'blur(12px)',
                cursor: 'pointer',
              }}
            >
              <Brain className="w-4 h-4" style={{ color: cinematicDone ? 'rgba(255,255,255,0.6)' : '#60a5fa' }} />
              <span className="text-[12px] font-medium" style={{ color: cinematicDone ? 'rgba(255,255,255,0.6)' : '#93c5fd' }}>
                {cinematicDone ? '37 capacites \u2014 Revoir' : 'Decouvrir les capacites'}
              </span>
            </button>
          )}
          {cinematicActive && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-[11px] text-blue-300/80">Sequence en cours...</span>
            </div>
          )}
          <SimulateButton token={token} />
        </div>
        )}

        {/* Mini-Chat: "Parler au cerveau" */}
        {!isGalaxyMode && !showAvatarChat && (
        <div
          className="absolute z-30"
          style={{
            bottom: chatOpen ? '80px' : '80px',
            left: chatOpen ? '16px' : '16px',
            width: chatOpen ? '400px' : 'auto',
          }}
          data-testid="brain-chat-panel"
        >
          {chatOpen ? (
            <div className="rounded-xl border border-blue-500/20 bg-black/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: '0 0 60px rgba(96,165,250,0.12), 0 0 15px rgba(96,165,250,0.08)' }}>
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-blue-500/5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                  <span className="text-[12px] font-semibold text-blue-300 tracking-wide uppercase">Interroger le cerveau</span>
                </div>
                <div className="flex items-center gap-2">
                  {clones.length > 1 && (
                    <select
                      value={selectedClone}
                      onChange={e => setSelectedClone(e.target.value)}
                      className="text-[10px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white/60 outline-none"
                      data-testid="brain-chat-clone-select"
                    >
                      {clones.map(c => (
                        <option key={c.id} value={c.id} className="bg-gray-900">{c.display_name || c.name}</option>
                      ))}
                    </select>
                  )}
                  <button onClick={() => setChatOpen(false)} className="text-white/30 hover:text-white/60 transition-colors">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="max-h-[220px] overflow-y-auto px-3 py-2 space-y-2 scrollbar-thin" data-testid="brain-chat-messages">
                {chatMessages.length === 0 && (
                  <p className="text-[11px] text-white/25 text-center py-6 italic">
                    Posez une question et observez le cerveau reflechir...
                  </p>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] px-3 py-2 rounded-lg text-[12px] leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-blue-500/15 text-blue-200 border border-blue-500/20'
                          : 'bg-white/5 text-white/70 border border-white/5'
                      }`}
                    >
                      {msg.content}
                      {msg.role === 'assistant' && msg.memoriesUsed != null && (
                        <div className="mt-1 text-[9px] text-white/25">
                          {msg.memoriesUsed} memoire(s) &middot; {msg.responseTimeMs}ms
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/5">
                      <Loader2 className="w-4 h-4 text-blue-400/50 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-t border-white/5">
                <input
                  type="text"
                  value={chatQuery}
                  onChange={e => setChatQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendBrainChat()}
                  placeholder="Interroger le clone..."
                  className="flex-1 bg-transparent text-[12px] text-white/80 placeholder-white/20 outline-none"
                  data-testid="brain-chat-input"
                  disabled={chatLoading}
                />
                <button
                  onClick={sendBrainChat}
                  disabled={chatLoading || !chatQuery.trim()}
                  className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 disabled:opacity-30 transition-colors"
                  data-testid="brain-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setChatOpen(true)}
              className="flex items-center gap-3 px-5 py-3 rounded-xl border border-blue-500/25 bg-black/60 backdrop-blur-sm hover:border-blue-400/40 hover:bg-blue-500/10 transition-all group"
              style={{ boxShadow: '0 0 30px rgba(96,165,250,0.08)' }}
              data-testid="brain-chat-open-btn"
            >
              <div className="w-2.5 h-2.5 rounded-full bg-blue-400 group-hover:animate-pulse" />
              <MessageSquare className="w-4 h-4 text-blue-400" />
              <span className="text-[13px] text-blue-300/80 font-semibold tracking-wide">Interroger le cerveau</span>
              <ChevronUp className="w-4 h-4 text-blue-400/40 group-hover:text-blue-400/70 transition-colors" />
            </button>
          )}
        </div>
        )}

        {/* Bottom: ECG Heartbeat */}
        {!isGalaxyMode && !showAvatarChat && (
        <div className="absolute bottom-4 left-4 right-4 z-10">
          <HeartbeatECG completedTasks={metrics?.completedTasks||0} failedTasks={metrics?.failedTasks||0} queueHealth={metrics?.queueHealth||0.8} />
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}
